// src/pages/PackagesPage.tsx
import React from 'react';
import { motion } from 'framer-motion';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PACKAGES from '../data/packagesData';
import type { StaticPackage } from '../data/packagesData';
import PackageCard from '../components/PackageCard';
import Seo from '../components/Seo';


const PackagesPage: React.FC = () => {
  // static page; no navigation required here

  // Use hard-coded static packages for UI/demo purposes (no API or DB)
  const packages: StaticPackage[] = PACKAGES;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-amber-900 to-orange-900 text-white">
      <Header />
      <Seo
        title="Jordan Tour Packages & Itineraries | Above & Below Adventures"
        description="Browse our curated Jordan tour packages—Petra, Wadi Rum, Dead Sea, and more. Transparent pricing, flexible dates, local experts."
        canonicalPath="/packages"
      />

      <main className="animate-gradient">
        <section className="container mx-auto px-4 py-16">
          {/* Page Title */}
          <div className="max-w-4xl mx-auto text-center mb-16">
            <motion.h1
              className="text-4xl md:text-5xl font-extrabold text-white mb-4 drop-shadow-lg"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              Discover Unforgettable Adventures
            </motion.h1>
            <motion.p
              className="text-lg md:text-xl text-amber-100 max-w-2xl mx-auto"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.6 }}
            >
              Explore our curated collection of premium tours designed to create lasting memories
            </motion.p>
          </div>

          {/* Category navigation */}
          <div className="mb-8 flex flex-wrap items-center justify-center gap-3">
            {['Aqaba Day Tours', 'Dead Sea Day Tours', 'Petra Day Tours', 'Wadi Rum Day Tours'].map((c) => (
              <a
                key={c}
                href={`#${c.replace(/\s+/g, '-').toLowerCase()}`}
                className="text-sm px-4 py-2 rounded-full bg-white/5 text-white border border-white/10 hover:bg-white/7"
              >
                {c.replace(' Day Tours', '')}
              </a>
            ))}
          </div>

          {/* Sections */}
          {['Aqaba Day Tours', 'Dead Sea Day Tours', 'Petra Day Tours', 'Wadi Rum Day Tours'].map((category) => {
            const list = packages.filter((p) => p.category === category);
            return (
              <section key={category} id={category.replace(/\s+/g, '-').toLowerCase()} className="mb-12">
                <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-6">{category}</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {list.map((pkg) => (
                    <PackageCard key={pkg.id} pkg={pkg} />
                  ))}
                </div>
              </section>
            );
          })}
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default PackagesPage;
